import {Inject, Injectable} from '@angular/core';

import {
    AbstractControl,
    AsyncValidatorFn,
    FormArray,
    FormGroup,
    FormGroupDirective,
    NgForm,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {Observable, of} from 'rxjs';
import {catchError, delay, finalize, map, switchMap, tap} from 'rxjs/operators';
import {
    FieldErrorObj,
    ValidationRegex,
    ValidationResponseObj,
    ValidationType
} from '../../../../generated/generated-core.model';
import {
    MX_VALIDATION_MANAGER_SERVICE_TOKEN,
    MxValidationManagerService
} from '../../../shared/services/mx-validation-manager.service';
import {MxPreloaderService} from '../../mx-preloader/services/mx-preloader.service';
import {DataValidator} from './mx-object-form-validator.service';


@Injectable({
    providedIn: 'root'
})
export class MxFormErrorMessageService implements ErrorStateMatcher {

    /** map of messages for build-in angular validators */
    errorMap: { [key: string]: (c: AbstractControl, errorPayload: any, name: string) => string } = {
        'required': (c: AbstractControl, errorPayload: any, name: string) =>
            `label.errors.mandatory`,
        'email': (c: AbstractControl, errorPayload: any, name: string) =>
            `${c.value} is not a valid email`,
        'min': (c: AbstractControl, errorPayload: any, name: string) =>
            `Field must be greater or equals than ${errorPayload['min']}`,
        'max': (c: AbstractControl, errorPayload: any, name: string) =>
            `Field must be equal to or less than ${errorPayload['max']}`,
        'minlength': (c: AbstractControl, errorPayload: any, name: string) =>
            `You have used ${errorPayload['actualLength']} out of ${errorPayload['requiredLength']} characters as min`,
        'maxlength': (c: AbstractControl, errorPayload: any, name: string) =>
            `You have used ${errorPayload['actualLength']} out of ${errorPayload['requiredLength']} characters as max`,
        'matDatepickerParse' : (c: AbstractControl, errorPayload: any, name: string) =>
            'Enter the date as MM/DD/YYYY. Thanks!',
        'matDatepickerMin': (c: AbstractControl, errorPayload: any, name: string) =>
            `Date is less than allowed`,
        'defaultValue': (c: AbstractControl, errorPayload: any, name: string) =>
            `Field is required. Default value is ${errorPayload.value}`,
        'matDatepickerMax': (c: AbstractControl, errorPayload: any, name: string) =>
            `Date is greater than allowed`,
        'matchPassword': (c: AbstractControl, errorPayload: any, name: string) =>
            `label.errors.retyped.value.different`,
        'isOutputsContainXAxis': (c: AbstractControl, errorPayload: any, name: string) =>
            `X Axis should not be in the output fields`
    };

    constructor(
            @Inject(MX_VALIDATION_MANAGER_SERVICE_TOKEN) private validationManagerService: MxValidationManagerService,
            private preloader: MxPreloaderService) {
    }

    // Async validator to be used in forms
    public asyncValidator(validator: DataValidator<any>, url?: string): AsyncValidatorFn {
        return (control: FormGroup): Observable<ValidationErrors | null> => {
            return of(control.getRawValue())
                .pipe(
                    delay(500),
                    tap(() => this.preloader.show()),
                    switchMap((value) => validator.validate(value, url)),
                    map(r => {
                        if (r.fieldErrors && (Object.keys(r.fieldErrors).length || Object.keys(r.globalErrors).length)) {
                            return this.getErrors(r);
                        } else {
                            return null;
                        }
                    }),
                    catchError(_e => of(void 0)),
                    finalize(() => this.preloader.hide())
                );
        };
    }

    public getErrors(response: ValidationResponseObj): any {
        const errors: object = {};
        for (let fieldName of Object.keys(response.fieldErrors)) {
            const fieldErrors: FieldErrorObj[] = response.fieldErrors[fieldName];
            for (let serverError of fieldErrors) {
                errors[fieldName] = {message: serverError.message};
            }
        }
        for (let error of response.globalErrors) {
            errors[error['code']] = {message: error['message']};
        }
        return errors;
    }

    public setServerErrors(response: ValidationResponseObj, from: AbstractControl): void {

        for (let fieldName of Object.keys(response.fieldErrors)) {
            const fieldErrors: FieldErrorObj[] = response.fieldErrors[fieldName];

            const errors = {};
            for (let serverError of fieldErrors) {
                errors[serverError.code] = {message: serverError.message};
            }

            const control = from.get([fieldName]);

            if (control && control.enabled) {
                control.setErrors(errors);
                control.markAsDirty();
            }
        }
    }

    public getMessage(field: string, form: FormGroup): string {
        return this.getMessages(field, form)[0] || '';
    }

    public getMessages(name: string, control: FormGroup): string[] {

        const ctrl = control.get([name]);

        let mapErrors = this.mapErrors(ctrl, name);
        let asyncError = this.getAsyncError(control, name);
        return [...mapErrors, ...asyncError];
    }

    private getAsyncError(control: FormGroup, name: string): string[] {
        if (!!control && !!control.errors && !!control.errors[name]) {
            return [control.errors[name].message];
        }
        return [];
    }

    private mapErrors(control: AbstractControl, name: string): string[] {
        return Object.keys(control.errors || {})
            .map(key => {
                return control.errors[key].message || this.getErrorFromMap(key, control, name);
            });
    }

    private getErrorFromMap(key: string, c: AbstractControl, name?: string, errorPayload?: any): string {
        if (this.errorMap.hasOwnProperty(key)) {
            let errors = errorPayload || c.errors[key];
            return this.errorMap[key](c, errors, name || 'Field');
        } else {
            return key;
        }
    }

    public isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
        let name = this.getName(control);
        const isSubmitted = form && form.submitted;
        return !!(control && (control.invalid || (form.invalid && form.errors && form.errors[name]))
            && (control.dirty || control.touched || isSubmitted));
    }

    private getName(control: AbstractControl): string | null {
        if (!control || !control.parent) {
            return null;
        }
        let group = <FormGroup>control.parent;

        let name: string;

        Object.keys(group.controls).forEach(key => {
            let childControl = group.get([key]);

            if (childControl !== control) {
                return;
            }

            name = key;
        });

        return name;
    }

    public arePresentFormControlsValid(form: FormGroup | FormArray): boolean {
        if (!form || form.pending || !form.touched) {
            return false;
        }

        let isValid = true;

        Object.keys(form.controls).forEach(key => {
            const control = form.get([key]);

            if (control instanceof FormGroup || control instanceof FormArray) {
                isValid = isValid && this.arePresentFormControlsValid(control);
            } else {
                isValid = isValid && !(control && (control.invalid || (form.invalid && form.errors && form.errors[key]))
                    && (control.dirty || control.touched));
            }
        });

        return isValid;
    }

    public boPatternValidator(patternKey: ValidationRegex): ValidatorFn {

        let boRegex = this.validationManagerService.getRegex(patternKey);
        let boError: ValidationErrors = {};

        if (boRegex) {
            return (c: AbstractControl): ValidationErrors | null => {

                let error = Validators.pattern(boRegex.regex)(c);

                if (error && error['pattern']) {
                    boError[patternKey] = {
                        message: boRegex.message,
                        requiredPattern: error['pattern'].requiredPattern,
                        actualValue: error['pattern'].actualValue
                    };
                    return boError;
                } else {
                    return null;
                }
            };
        } else {
            console.error(`Cannot find pattern ${patternKey}`);
            return Validators.nullValidator;
        }
    }


    public boValidationValidator(type: ValidationType, fieldName: string | null): ValidatorFn {

        let validator = this.validationManagerService.getValidator(type);

        if (validator) {

            if (validator.externalValidation && type !== 'EMAIL') {
                console.warn(`boValidationValidator ValidationType: ${type} is external and is not supported` );
            }

            return (c: AbstractControl): ValidationErrors | null => {
                let boError: ValidationErrors = {};

                let error = null;

                if (type === 'EMAIL') {
                    error =  Validators.email(c) || error;
                    if (error && error['email']) {
                        boError['email'] = error['email'];
                    }
                }

                if (validator.regexName) {

                    let boRegex = this.validationManagerService.getRegex(validator.regexName);

                    error = Validators.pattern(boRegex.regex)(c) || error;

                    if (error && error['pattern']) {
                        boError[type] = {
                            message: validator.message || boRegex.message,
                            requiredPattern: error['pattern'].requiredPattern,
                            actualValue: error['pattern'].actualValue
                        };
                    }
                }
                return boError;
            };
        } else {
            console.error(`Cannot find validator ${type}`);
            return Validators.nullValidator;
        }
    }

    public boDigitValidator(integerDigits: number, fractionDigits: number): ValidatorFn {

        if (typeof integerDigits !== 'number' || typeof fractionDigits !== 'number') {
            console.error('Wrong parameters in the validator Digits');
            return Validators.nullValidator;
        }

        let regex: RegExp;
        let errorMessage: string;

        if (integerDigits > 0 && fractionDigits === 0) {
            regex = new RegExp(`^\\d{1,${integerDigits}}$`);
            errorMessage = `Field must contain only whole numbers with maximum ${integerDigits} digits`;
        } else if (integerDigits > 0 && fractionDigits > 0) {
            regex = new RegExp(`^\\d{1,${integerDigits}}(\\.\\d{1,${fractionDigits}})?$`);
            errorMessage = `Field must contain maximum ${integerDigits} digits before decimal point
                and maximum ${fractionDigits} digits after decimal point`;
        } else {
            console.error('Wrong parameters in the validator Digits');
        }

        return (c: AbstractControl): ValidationErrors | null => {
            let error = Validators.pattern(regex)(c);
            let boError: ValidationErrors = {};

            if (error && error['pattern']) {
                boError['digits'] = {
                    message: errorMessage,
                    requiredPattern: error['pattern'].requiredPattern,
                    actualValue: error['pattern'].actualValue
                };
                return boError;
            } else {
                return null;
            }
        };
    }

    minLengthValidator(minLength: number, type: ValidationType): ValidatorFn {
        let minTypeLength: number;
        if (type) {
            minTypeLength =  this.validationManagerService.getValidator(type).minLength;
        }
        let min: number;
        if (minLength && minTypeLength ) {
          min = Math.max(minLength, minTypeLength);
        } else if (minLength && !minTypeLength) {
            min = minLength;
        } else if (!minLength && minTypeLength) {
            min = minTypeLength;
        }
        if (min && min > 0) {
            return Validators.minLength(min);
        }
    }

    maxLengthValidator(maxLength: number, type: ValidationType): ValidatorFn {
        let maxTypeLength: number;
        if (type) {
            maxTypeLength =  this.validationManagerService.getValidator(type).maxLength;
        }
        let max: number;
        if (maxLength && maxTypeLength ) {
            max = Math.min(maxLength, maxTypeLength);
        } else if (maxLength && !maxTypeLength) {
            max = maxLength;
        } else if (!maxLength && maxTypeLength) {
            max = maxTypeLength;
        }

        if (max && max > 0) {
            return Validators.maxLength(max);
        }
    }

    requiredValidator(required: boolean, type: ValidationType): ValidatorFn {
        let requiredValue: boolean;
        if (type) {
            requiredValue =  this.validationManagerService.getValidator(type).mandatory;
        }
        if (required === true || requiredValue === true) {
            return Validators.required;
        }
    }

    defaultValueValidator(defaultValue: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const isEmpty = control.value == null || control.value.length === 0;
            if (isEmpty) {
                return {defaultValue: {value: defaultValue}};
            }
            return null;
        };
    }
}
